interface CaseWorkflowTemplateLike {
  stages_json: unknown;
}

export interface CaseWorkflowStage {
  key: string;
  label: string;
}

export interface DerivedCaseWorkflow {
  stages: CaseWorkflowStage[];
  currentStageKey: string;
  currentStageLabel: string;
  currentStageIndex: number;
  currentStageNumber: number;
  totalStages: number;
  nextStageKey: string | null;
  nextStageLabel: string | null;
  previousStageKey: string | null;
  previousStageLabel: string | null;
}

const toLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeStageLabelToken = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

export const normalizeCaseStageToken = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const extractTemplateStageRows = (
  stagesJson: unknown
): Array<{ key: string; label: string; order?: number; insertion: number }> => {
  let rawStages: unknown[] = [];
  if (Array.isArray(stagesJson)) {
    rawStages = stagesJson;
  } else if (
    typeof stagesJson === "object" &&
    stagesJson !== null &&
    "stages" in (stagesJson as Record<string, unknown>) &&
    Array.isArray((stagesJson as Record<string, unknown>).stages)
  ) {
    rawStages = (stagesJson as { stages: unknown[] }).stages;
  }

  const rows: Array<{ key: string; label: string; order?: number; insertion: number }> = [];
  rawStages.forEach((item, index) => {
    if (typeof item !== "object" || item === null) return;
    const row = item as Record<string, unknown>;
    const rawKey = row.key;
    if (typeof rawKey !== "string" || !rawKey.trim()) return;
    const key = rawKey.trim();
    const rawLabel = row.label;
    const label = typeof rawLabel === "string" && rawLabel.trim() ? rawLabel.trim() : toLabel(key);
    const rawOrder = row.order;
    const order = typeof rawOrder === "number" ? rawOrder : undefined;
    rows.push({
      key,
      label,
      order,
      insertion: index,
    });
  });

  rows.sort((left, right) => {
    if (left.order == null && right.order == null) return left.insertion - right.insertion;
    if (left.order == null) return 1;
    if (right.order == null) return -1;
    if (left.order !== right.order) return left.order - right.order;
    return left.insertion - right.insertion;
  });

  return rows;
};

export const extractCaseWorkflowStages = (
  template?: CaseWorkflowTemplateLike | null
): CaseWorkflowStage[] => {
  if (!template) return [];
  const rows = extractTemplateStageRows(template.stages_json);

  const deduped: CaseWorkflowStage[] = [];
  const seen = new Set<string>();
  rows.forEach((row) => {
    const normalizedKey = normalizeCaseStageToken(row.key);
    if (!normalizedKey || seen.has(normalizedKey)) return;
    seen.add(normalizedKey);
    deduped.push({
      key: row.key,
      label: row.label,
    });
  });
  return deduped;
};

export const deriveCaseWorkflow = (
  lifecycleStatus: string | null | undefined,
  currentStageLabel: string | null | undefined,
  template?: CaseWorkflowTemplateLike | null
): DerivedCaseWorkflow => {
  const stages = extractCaseWorkflowStages(template);
  if (stages.length === 0) {
    const fallbackKey = normalizeCaseStageToken(lifecycleStatus) || "current_stage";
    const fallbackLabel =
      (currentStageLabel ?? "").trim() || toLabel(fallbackKey || "current_stage");
    return {
      stages: [{ key: fallbackKey, label: fallbackLabel }],
      currentStageKey: fallbackKey,
      currentStageLabel: fallbackLabel,
      currentStageIndex: 0,
      currentStageNumber: 1,
      totalStages: 1,
      nextStageKey: null,
      nextStageLabel: null,
      previousStageKey: null,
      previousStageLabel: null,
    };
  }

  const normalizedLifecycle = normalizeCaseStageToken(lifecycleStatus);
  const normalizedCurrentLabel = normalizeStageLabelToken(currentStageLabel);

  let currentStageIndex = stages.findIndex(
    (stage) => normalizeCaseStageToken(stage.key) === normalizedLifecycle
  );
  if (currentStageIndex < 0 && normalizedCurrentLabel) {
    currentStageIndex = stages.findIndex(
      (stage) => normalizeStageLabelToken(stage.label) === normalizedCurrentLabel
    );
  }
  if (currentStageIndex < 0) {
    currentStageIndex = 0;
  }

  const currentStage = stages[currentStageIndex] ?? stages[0];
  const nextStage = stages[currentStageIndex + 1] ?? null;
  const previousStage = currentStageIndex > 0 ? stages[currentStageIndex - 1] : null;

  return {
    stages,
    currentStageKey: currentStage.key,
    currentStageLabel: (currentStageLabel ?? "").trim() || currentStage.label,
    currentStageIndex,
    currentStageNumber: currentStageIndex + 1,
    totalStages: stages.length,
    nextStageKey: nextStage?.key ?? null,
    nextStageLabel: nextStage?.label ?? null,
    previousStageKey: previousStage?.key ?? null,
    previousStageLabel: previousStage?.label ?? null,
  };
};
